import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { BrokerCard } from '@/components/BrokerCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { brokerAPI } from '@/services/api';
import { BrokerFinderForm } from '@/components/BrokerFinderForm';
import { 
  TrendingUp, 
  Shield, 
  Zap, 
  Users, 
  ArrowRight,
  CheckCircle2,
  Star,
  BarChart3,
  Search,
  Award,
  Loader2
} from 'lucide-react';
import { useSeo } from '@/lib/useSeo';

const HomePage = () => {
  useSeo({ title: 'Neobroker.com – Online-Broker & Neobroker im Vergleich', description: 'Online-Broker und Neobroker unabhängig vergleichen: Gebühren, Mindesteinlage, Regulierung und echte Erfahrungen – finde den passenden Broker auf Neobroker.com.' });
  const [topBrokers, setTopBrokers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrokers = async () => {
      const data = await brokerAPI.getBrokers();
      // Sort by rating and take top 3
      const sorted = data.sort((a, b) => b.rating - a.rating).slice(0, 3);
      setTopBrokers(sorted);
      setLoading(false);
    };
    fetchBrokers();
  }, []);
  
  const features = [
    {
      icon: BarChart3,
      title: 'Real-Time Comparison',
      description: 'Compare fees, spreads, and features across hundreds of brokers instantly.'
    },
    {
      icon: Shield,
      title: 'Verified Reviews',
      description: 'Read authentic reviews from real traders to make confident decisions.'
    },
    {
      icon: Zap,
      title: 'AI-Powered Insights',
      description: 'Get personalized broker recommendations based on your trading style.'
    },
    {
      icon: Users,
      title: 'Community Driven',
      description: 'Join thousands of traders sharing insights and strategies daily.'
    }
  ];
  
  return (
    <div className="min-h-screen bg-background pt-1" data-testid="home-page">
      <Helmet>
        <title>Neobroker.com – Online-Broker & Neobroker im Vergleich</title>
      </Helmet>
      <Navigation />
      
      {/* Hero Section - Original Style with Glass */}
      <section className="relative overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(210,100%,97%)] via-[hsl(190,95%,97%)] to-background dark:from-[hsl(220,18%,12%)] dark:via-[hsl(220,18%,10%)] dark:to-background -z-10" />
        
        {/* Subtle static orbs (no continuous animation -> stable Speed Index) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[hsl(210,100%,56%)]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[hsl(190,95%,55%)]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Badge */}
            <Badge 
              variant="outline" 
              className="backdrop-blur-md bg-white/70 dark:bg-white/10 border-white/20 dark:border-white/20 text-[hsl(210,100%,45%)] dark:text-[hsl(190,95%,70%)] px-4 py-1.5 shadow-lg"
            >
              <Star className="w-3 h-3 mr-1 fill-[hsl(38,92%,50%)] text-[hsl(38,92%,50%)]" />
              Trusted by 100,000+ traders worldwide
            </Badge>
            
            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight tracking-tight pb-4">
              Find Your Perfect
              <span className="block mt-3 pb-2 bg-gradient-to-r from-[hsl(210,100%,56%)] to-[hsl(190,95%,55%)] bg-clip-text text-transparent">
                Trading Partner
              </span>
            </h1>
            
            {/* Subheading */}
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Compare fees, features, and reviews from the world's leading brokers. 
              Make informed decisions with real-time data and AI-powered insights.
            </p>

            <BrokerFinderForm />
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link to="/compare">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-[hsl(210,100%,56%)] to-[hsl(190,95%,55%)] hover:opacity-90 transition-opacity text-base px-8 h-12"
                >
                  Compare Brokers
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/trading-guide">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="text-base px-8 h-12 backdrop-blur-md bg-white/50 dark:bg-white/5 border-white/30 dark:border-white/10"
                >
                  <Search className="mr-2 w-5 h-5" />
                  Free Trading Guide
                </Button>
              </Link>
            </div>
            
            {/* Trust Indicators - Glass Style */}
            <div className="flex flex-wrap items-center justify-center gap-6 pt-8 text-sm text-foreground/70">
              <div className="backdrop-blur-xl bg-white/50 dark:bg-white/5 border border-white/30 dark:border-white/10 rounded-full px-6 py-3 shadow-lg flex items-center space-x-2 hover:shadow-xl transition-shadow">
                <CheckCircle2 className="w-4 h-4 text-[hsl(142,76%,45%)]" />
                <span className="font-medium text-foreground">Free Comparison</span>
              </div>
              <div className="backdrop-blur-xl bg-white/50 dark:bg-white/5 border border-white/30 dark:border-white/10 rounded-full px-6 py-3 shadow-lg flex items-center space-x-2 hover:shadow-xl transition-shadow">
                <CheckCircle2 className="w-4 h-4 text-[hsl(142,76%,45%)]" />
                <span className="font-medium text-foreground">Unbiased Reviews</span>
              </div>
              <div className="backdrop-blur-xl bg-white/50 dark:bg-white/5 border border-white/30 dark:border-white/10 rounded-full px-6 py-3 shadow-lg flex items-center space-x-2 hover:shadow-xl transition-shadow">
                <CheckCircle2 className="w-4 h-4 text-[hsl(142,76%,45%)]" />
                <span className="font-medium text-foreground">Real-Time Data</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section - Glass Style */}
      <section className="py-20 bg-background relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 backdrop-blur-xl bg-[hsl(210,100%,56%)]/10 border-[hsl(210,100%,56%)]/20 text-[hsl(210,100%,45%)] dark:text-[hsl(190,95%,70%)]">
              <Zap className="w-3 h-3 mr-1" />
              Why Choose Us
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Everything You Need to Trade Smarter
            </h2>
            <p className="text-foreground/70 text-lg max-w-2xl mx-auto">
              Powerful tools and insights to help you find the perfect broker
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-sm sm:max-w-none mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group relative transition-transform duration-300 hover:-translate-y-1"
              >
                {/* Blue line border on hover - EXACT same as AnimatedCard/Broker Cards */}
                <div className="absolute -inset-[1px] bg-gradient-to-r from-[hsl(210,100%,56%)] via-[hsl(190,95%,55%)] to-[hsl(210,100%,56%)] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Card content - EXACT same style as AnimatedCard */}
                <div className="relative rounded-2xl p-6 h-full bg-white border border-gray-200 shadow-[0_4px_20px_rgba(0,0,0,0.08)] group-hover:border-[hsl(210,100%,60%)]/60 dark:bg-[hsl(220,18%,13%)] dark:border-[hsl(210,100%,60%)]/30 dark:shadow-none dark:group-hover:border-[hsl(210,100%,60%)]/70">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[hsl(210,100%,90%)] to-[hsl(190,95%,90%)] dark:from-[hsl(210,100%,20%)] dark:to-[hsl(190,95%,20%)] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <feature.icon className="w-7 h-7 text-[hsl(210,100%,45%)]" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground/70 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Top Brokers Section - Glass Style */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-[hsl(210,100%,97%)] to-background dark:from-background dark:via-[hsl(220,25%,8%)] dark:to-background -z-10" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <Badge className="mb-4 backdrop-blur-xl bg-[hsl(38,92%,50%)]/10 border-[hsl(38,92%,50%)]/20 text-[hsl(38,70%,40%)] dark:text-[hsl(38,92%,60%)]">
                <Award className="w-3 h-3 mr-1" />
                Top Rated
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">
                Featured Brokers
              </h2>
              <p className="text-foreground/70 max-w-lg">
                Hand-picked brokers with the best ratings, lowest fees, and most features.
              </p>
            </div>
            <Link to="/compare">
              <Button 
                variant="outline" 
                className="group backdrop-blur-xl bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 rounded-xl"
              >
                View All Brokers
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-sm md:max-w-none mx-auto">
            {loading ? (
              <div className="col-span-full flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[hsl(210,100%,56%)]" />
              </div>
            ) : topBrokers.length > 0 ? (
              topBrokers.map((broker, index) => (
                <BrokerCard key={broker.id} broker={broker} featured={index === 0} />
              ))
            ) : (
              <div className="col-span-full text-center py-12 text-foreground/70">
                No brokers available yet.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Trading Guide CTA Section */}
      <section className="py-20 bg-muted/30 dark:bg-muted/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="group relative">
              {/* Subtle outline on hover - both modes */}
              <div className="absolute -inset-[1px] bg-gradient-to-r from-[hsl(210,100%,56%)] via-[hsl(190,95%,55%)] to-[hsl(210,100%,56%)] rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Card with subtle blue outline on hover - NO glow fill */}
              <div className="relative rounded-3xl p-8 sm:p-12 text-center transition-all duration-300 bg-white border border-gray-200 shadow-[0_4px_20px_rgba(0,0,0,0.08)] group-hover:border-[hsl(210,100%,60%)]/60 dark:bg-[hsl(220,18%,13%)] dark:border-[hsl(210,100%,60%)]/30 dark:shadow-none dark:group-hover:border-[hsl(210,100%,60%)]/70">
                <Badge variant="outline" className="mb-4 bg-white dark:bg-transparent border-gray-200 dark:border-[hsl(210,100%,60%)]/30">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Free Trading Education
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                  Get Your Free Trading Guide
                </h2>
                <p className="text-foreground/70 text-lg mb-8 max-w-xl mx-auto">
                  Learn how to choose the best broker, manage risk, and start trading successfully with our comprehensive guide.
                </p>
                <div className="flex flex-wrap justify-center gap-4 mb-8">
                  {['Broker Selection', 'Risk Management', 'Trading Strategies', 'Exclusive Bonuses'].map((item, index) => (
                    <div key={index} className="flex items-center space-x-2 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-[hsl(210,100%,60%)]/20 rounded-full px-4 py-2">
                      <CheckCircle2 className="w-4 h-4 text-[hsl(142,76%,45%)]" />
                      <span className="text-sm text-foreground/80">{item}</span>
                    </div>
                  ))}
                </div>
                <Link to="/trading-guide">
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-[hsl(210,100%,56%)] to-[hsl(190,95%,55%)] hover:opacity-90 transition-all shadow-lg hover:shadow-2xl hover:scale-105"
                  >
                    Get Free Guide
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default HomePage;
